import React from 'react';

/**
 * Common Skeleton Primitive with subtle shimmer pulse
 */
export const SkeletonBox: React.FC<{ className?: string; style?: React.CSSProperties }> = ({
  className = '',
  style,
}) => (
  <div
    className={`bg-slate-200/80 animate-pulse rounded-xl ${className}`}
    style={style}
  />
);

/**
 * BookingCardSkeleton - Matches the design of ClientBookingList cards
 */
export const BookingCardSkeleton: React.FC = () => (
  <div className="bg-white rounded-3xl p-5 border border-[#E0E6ED] shadow-2xs space-y-3.5 animate-pulse">
    {/* Top Row: Category Pill & Status Pill */}
    <div className="flex items-center justify-between gap-2">
      <SkeletonBox className="h-6 w-24 rounded-full bg-orange-100/70" />
      <SkeletonBox className="h-6 w-28 rounded-full bg-emerald-100/70" />
    </div>

    {/* Title: Venue Name */}
    <SkeletonBox className="h-6 w-2/3 rounded-lg" />

    {/* Metadata Rows (Date, Time, Field) */}
    <div className="space-y-2 pt-1">
      <div className="flex items-center gap-2">
        <SkeletonBox className="w-4 h-4 rounded-full shrink-0" />
        <SkeletonBox className="h-4 w-44 rounded-md" />
      </div>
      <div className="flex items-center gap-2">
        <SkeletonBox className="w-4 h-4 rounded-full shrink-0" />
        <SkeletonBox className="h-4 w-36 rounded-md" />
      </div>
      <div className="flex items-center gap-2">
        <SkeletonBox className="w-4 h-4 rounded-full shrink-0" />
        <SkeletonBox className="h-4 w-28 rounded-md" />
      </div>
    </div>

    {/* Divider & Bottom Row */}
    <div className="border-t border-[#E0E6ED] pt-3 flex items-center justify-between gap-3">
      <div className="space-y-1">
        <SkeletonBox className="h-3 w-10 rounded-md" />
        <SkeletonBox className="h-6 w-28 rounded-md bg-orange-100/70" />
      </div>
      <SkeletonBox className="h-9 w-24 rounded-full" />
    </div>
  </div>
);

/**
 * BookingDetailSkeleton - Matches the exact layout of ClientBookingDetail page
 */
export const BookingDetailSkeleton: React.FC = () => (
  <div className="max-w-2xl mx-auto px-4 pt-18 md:pt-6 space-y-5 animate-pulse">
    {/* 1. Dark Radial Gradient Header Card Skeleton */}
    <div
      className="shadow-md overflow-hidden"
      style={{
        borderRadius: '18px',
        padding: '16px',
        background: 'radial-gradient(120% 90% at 100% 0%, rgb(37, 49, 74) 0%, rgb(14, 23, 38) 65%)',
        display: 'flex',
        flexDirection: 'column',
        gap: '14px',
      }}
    >
      {/* Top Row: Invoice & Status Badge */}
      <div className="flex items-start justify-between gap-2">
        <div className="space-y-1">
          <SkeletonBox className="h-3 w-20 bg-white/20 rounded-md" />
          <SkeletonBox className="h-6 w-36 bg-white/30 rounded-md" />
        </div>
        <SkeletonBox className="h-6 w-16 bg-white/20 rounded-md" />
      </div>

      <div className="border-t border-white/10" />

      {/* Arena Info Row */}
      <div className="flex items-center gap-3">
        <SkeletonBox className="w-12 h-12 rounded-xl bg-white/20 shrink-0" />
        <div className="space-y-2 flex-1">
          <SkeletonBox className="h-5 w-3/4 bg-white/30 rounded-md" />
          <SkeletonBox className="h-3 w-1/2 bg-white/20 rounded-md" />
        </div>
      </div>
    </div>

    {/* 2. Specification Table Card Skeleton */}
    <div className="bg-white rounded-2xl border border-[#E0E6ED] shadow-2xs divide-y divide-gray-100 overflow-hidden">
      {[1, 2, 3, 4, 5].map((i) => (
        <div key={i} className="flex items-center justify-between p-4">
          <div className="flex items-center gap-2.5">
            <SkeletonBox className="w-4 h-4 rounded-full" />
            <SkeletonBox className="h-4 w-20 rounded-md" />
          </div>
          <SkeletonBox className="h-4 w-32 rounded-md" />
        </div>
      ))}
    </div>

    {/* 3. Price Breakdown Section Skeleton */}
    <div className="space-y-3">
      <SkeletonBox className="h-5 w-28 rounded-md" />
      <div className="bg-white rounded-2xl border border-[#E0E6ED] shadow-2xs p-4 space-y-3.5">
        <div className="flex items-center justify-between">
          <SkeletonBox className="h-4 w-36 rounded-md" />
          <SkeletonBox className="h-4 w-24 rounded-md" />
        </div>
        <div className="flex items-center justify-between">
          <SkeletonBox className="h-4 w-28 rounded-md" />
          <SkeletonBox className="h-4 w-20 rounded-md" />
        </div>
        <div className="flex items-center justify-between">
          <SkeletonBox className="h-4 w-32 rounded-md" />
          <SkeletonBox className="h-4 w-20 rounded-md" />
        </div>
        <div className="border-t border-gray-100 pt-3 flex items-center justify-between">
          <SkeletonBox className="h-5 w-16 rounded-md" />
          <SkeletonBox className="h-7 w-28 rounded-md bg-orange-100/70" />
        </div>
      </div>
    </div>
  </div>
);

/**
 * DashboardClientSkeleton - Smooth skeleton for ClientDashboard
 */
export const DashboardClientSkeleton: React.FC = () => (
  <div className="space-y-5 animate-pulse max-w-5xl mx-auto px-4 pt-4 md:pt-6">
    {/* Greeting Header Skeleton */}
    <div className="bg-gradient-to-r from-orange-500 to-orange-700 rounded-3xl p-6 text-white shadow-md space-y-4">
      <SkeletonBox className="h-4 w-32 bg-white/30 rounded-md" />
      <SkeletonBox className="h-8 w-64 bg-white/40 rounded-lg" />
      <div className="flex gap-3 pt-2">
        <SkeletonBox className="h-10 w-32 bg-white/30 rounded-full" />
        <SkeletonBox className="h-10 w-32 bg-white/20 rounded-full" />
      </div>
    </div>

    {/* Quick Action Icon Grid Skeleton */}
    <div className="grid grid-cols-4 gap-3 bg-white rounded-3xl p-4 border border-[#E0E6ED]">
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="flex flex-col items-center gap-2">
          <SkeletonBox className="w-12 h-12 rounded-2xl" />
          <SkeletonBox className="h-3 w-14 rounded-md" />
        </div>
      ))}
    </div>

    {/* Section: Agenda / Mabar Terdekat */}
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <SkeletonBox className="h-6 w-36 rounded-md" />
        <SkeletonBox className="h-4 w-16 rounded-md" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {[1, 2].map((i) => (
          <div key={i} className="bg-white rounded-3xl p-5 border border-[#E0E6ED] space-y-3">
            <div className="flex justify-between">
              <SkeletonBox className="h-5 w-24 rounded-full" />
              <SkeletonBox className="h-5 w-20 rounded-full" />
            </div>
            <SkeletonBox className="h-6 w-3/4 rounded-md" />
            <SkeletonBox className="h-4 w-1/2 rounded-md" />
            <div className="border-t border-gray-100 pt-3 flex justify-between">
              <SkeletonBox className="h-5 w-24 rounded-md" />
              <SkeletonBox className="h-8 w-24 rounded-full" />
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

/**
 * MabarCardSkeleton - Skeleton for ClientMabarPage mabar items
 */
export const MabarCardSkeleton: React.FC = () => (
  <div className="bg-white rounded-3xl border border-[#E0E6ED] shadow-2xs overflow-hidden space-y-3 p-4 animate-pulse">
    {/* Image & Category Badge */}
    <SkeletonBox className="h-40 w-full rounded-2xl" />

    {/* Title & Host info */}
    <div className="flex items-center gap-3">
      <SkeletonBox className="w-10 h-10 rounded-full shrink-0" />
      <div className="space-y-1.5 flex-1">
        <SkeletonBox className="h-5 w-3/4 rounded-md" />
        <SkeletonBox className="h-3 w-1/2 rounded-md" />
      </div>
    </div>

    {/* Schedule & Location */}
    <div className="space-y-2 pt-1">
      <SkeletonBox className="h-4 w-2/3 rounded-md" />
      <SkeletonBox className="h-4 w-1/2 rounded-md" />
    </div>

    {/* Price & Slot Bar */}
    <div className="border-t border-gray-100 pt-3 flex items-center justify-between">
      <div className="space-y-1">
        <SkeletonBox className="h-3 w-12 rounded-md" />
        <SkeletonBox className="h-6 w-24 rounded-md bg-orange-100/70" />
      </div>
      <SkeletonBox className="h-9 w-28 rounded-full" />
    </div>
  </div>
);

/**
 * CommunityCardSkeleton - Skeleton for ClientMyCommunityPage
 */
export const CommunityCardSkeleton: React.FC = () => (
  <div className="bg-white rounded-3xl p-5 border border-[#E0E6ED] shadow-2xs space-y-4 animate-pulse">
    <div className="flex items-center gap-3">
      <SkeletonBox className="w-12 h-12 rounded-2xl shrink-0" />
      <div className="space-y-1.5 flex-1">
        <SkeletonBox className="h-5 w-2/3 rounded-md" />
        <SkeletonBox className="h-3 w-1/3 rounded-md" />
      </div>
      <SkeletonBox className="h-8 w-20 rounded-full" />
    </div>
    <SkeletonBox className="h-4 w-full rounded-md" />
  </div>
);

/**
 * CommunityListSkeleton - Skeleton for ClientMyCommunityPage list items
 */
export const CommunityListSkeleton: React.FC = () => (
  <div className="space-y-5 animate-pulse">
    {/* SECTION 1 SKELETON */}
    <div className="space-y-2.5">
      <SkeletonBox className="h-4 w-28 rounded-md" />
      <div className="bg-white rounded-2xl p-2 sm:p-3 border border-[#E0E6ED] divide-y divide-gray-100 shadow-2xs">
        {[1, 2].map((i) => (
          <div key={i} className="py-3 px-2 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <SkeletonBox className="w-11 h-11 rounded-full shrink-0" />
              <div className="space-y-1.5 flex-1 min-w-0">
                <SkeletonBox className="h-4 w-36 rounded-md" />
                <SkeletonBox className="h-3 w-24 rounded-md" />
              </div>
            </div>
            <SkeletonBox className="h-9 w-24 rounded-xl shrink-0" />
          </div>
        ))}
      </div>
    </div>

    {/* SECTION 2 SKELETON */}
    <div className="space-y-2.5 pt-2">
      <SkeletonBox className="h-4 w-44 rounded-md" />
      <div className="bg-white rounded-2xl p-2 sm:p-3 border border-[#E0E6ED] divide-y divide-gray-100 shadow-2xs">
        {[1, 2, 3].map((i) => (
          <div key={i} className="py-3 px-2 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <SkeletonBox className="w-11 h-11 rounded-full shrink-0" />
              <div className="space-y-1.5 flex-1 min-w-0">
                <SkeletonBox className="h-4 w-40 rounded-md" />
                <SkeletonBox className="h-3 w-28 rounded-md" />
              </div>
            </div>
            <SkeletonBox className="h-9 w-20 rounded-xl shrink-0 bg-orange-100/80" />
          </div>
        ))}
      </div>
    </div>
  </div>
);

/**
 * CommunityMembersSkeleton - Matches the design of ClientCommunityMembersPage
 */
export const CommunityMembersSkeleton: React.FC = () => (
  <div className="space-y-4 animate-pulse">
    {/* Count Info Bar */}
    <div className="flex items-center justify-between px-1">
      <SkeletonBox className="h-4 w-36 rounded-md" />
      <SkeletonBox className="h-4 w-20 rounded-md bg-orange-100/70" />
    </div>

    {/* Members Card List */}
    <div className="bg-white rounded-3xl border border-[#E0E6ED] shadow-2xs divide-y divide-gray-100 overflow-hidden">
      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((i) => (
        <div key={i} className="p-3.5 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <SkeletonBox className="w-4 h-4 rounded shrink-0" />
            <SkeletonBox className="w-10 h-10 rounded-full shrink-0" />
            <div className="space-y-1.5 flex-1 min-w-0">
              <SkeletonBox className="h-4 w-32 rounded-md" />
              <SkeletonBox className="h-3 w-24 rounded-md" />
            </div>
          </div>
          <SkeletonBox className="h-6 w-14 rounded-full shrink-0" />
        </div>
      ))}
    </div>

    {/* Pagination Bar */}
    <div className="flex items-center justify-center gap-2 pt-2 pb-6">
      <SkeletonBox className="w-8 h-8 rounded-full shrink-0" />
      {[1, 2, 3, 4, 5].map((i) => (
        <SkeletonBox key={i} className="w-8 h-8 rounded-full shrink-0" />
      ))}
      <SkeletonBox className="w-8 h-8 rounded-full shrink-0" />
    </div>
  </div>
);

/**
 * CommunityEventsSkeleton - Matches the design of ClientCommunityEventsPage
 */
export const CommunityEventsSkeleton: React.FC = () => (
  <div className="space-y-4 animate-pulse">
    {/* Count Info Bar */}
    <div className="flex items-center justify-between px-1">
      <SkeletonBox className="h-4 w-32 rounded-md" />
      <SkeletonBox className="h-4 w-20 rounded-md bg-orange-100/70" />
    </div>

    {/* Event Cards List */}
    <div className="space-y-3.5">
      {[1, 2, 3, 4, 5, 6].map((i) => (
        <div
          key={i}
          className="bg-white rounded-3xl p-4 border border-[#E0E6ED] shadow-2xs space-y-3"
        >
          <div className="flex items-start gap-3">
            {/* Left Date Box */}
            <div className="w-16 h-18 rounded-2xl bg-orange-50/80 p-2 flex flex-col items-center justify-center shrink-0 space-y-1">
              <SkeletonBox className="h-3 w-8 bg-orange-200/70 rounded-md" />
              <SkeletonBox className="h-6 w-10 bg-orange-300/80 rounded-md" />
              <SkeletonBox className="h-3 w-8 bg-orange-200/70 rounded-md" />
            </div>
            {/* Right Info */}
            <div className="space-y-2 flex-1 min-w-0 pt-0.5">
              <SkeletonBox className="h-5 w-3/4 rounded-md" />
              <SkeletonBox className="h-3.5 w-44 rounded-md" />
              <SkeletonBox className="h-3.5 w-32 rounded-md" />
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between pt-1">
            <SkeletonBox className="h-7 w-20 rounded-full bg-orange-100/70" />
            <SkeletonBox className="h-8 w-28 rounded-full border border-orange-200" />
          </div>
        </div>
      ))}
    </div>

    {/* Pagination Bar */}
    <div className="flex items-center justify-center gap-2 pt-2 pb-6">
      <SkeletonBox className="w-8 h-8 rounded-full shrink-0" />
      {[1, 2, 3, 4, 5].map((i) => (
        <SkeletonBox key={i} className="w-8 h-8 rounded-full shrink-0" />
      ))}
      <SkeletonBox className="w-8 h-8 rounded-full shrink-0" />
    </div>
  </div>
);

/**
 * MabarDetailSkeleton - Matches the exact layout of ClientMabarDetailPage
 */
export const MabarDetailSkeleton: React.FC = () => (
  <div className="min-h-screen bg-[#F8FAFC] max-w-6xl mx-auto px-4 md:px-6 py-4 md:py-6 relative pb-28 lg:pb-12 space-y-6 animate-pulse">
    {/* Desktop Breadcrumb Header Skeleton */}
    <div className="hidden md:flex items-center justify-between mb-4">
      <div className="flex items-center gap-3">
        <SkeletonBox className="w-10 h-10 rounded-full" />
        <div className="space-y-1.5">
          <SkeletonBox className="h-5 w-44 rounded-md" />
          <SkeletonBox className="h-3 w-64 rounded-md" />
        </div>
      </div>
      <div className="flex items-center gap-2">
        <SkeletonBox className="h-10 w-24 rounded-full" />
        <SkeletonBox className="h-10 w-10 rounded-full" />
      </div>
    </div>

    {/* Responsive Grid */}
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-start">
      {/* Left Column */}
      <div className="lg:col-span-7 xl:col-span-8 space-y-4">
        {/* HEADER SECTION: Badges & Title */}
        <div className="bg-white rounded-3xl border border-[#E0E6ED] p-5 space-y-3">
          <div className="flex items-center gap-2">
            <SkeletonBox className="h-6 w-20 rounded-lg bg-emerald-100/70" />
            <SkeletonBox className="h-6 w-24 rounded-lg bg-orange-100/70" />
          </div>
          <SkeletonBox className="h-8 w-3/4 rounded-lg" />
          <div className="flex items-center gap-2 pt-1">
            <div className="flex -space-x-2">
              {[1, 2, 3, 4, 5].map((i) => (
                <SkeletonBox key={i} className="w-7 h-7 rounded-full border-2 border-white shrink-0" />
              ))}
            </div>
            <SkeletonBox className="h-4 w-32 rounded-md" />
          </div>
        </div>

        {/* Schedule & Facilities */}
        <div className="bg-white rounded-3xl border border-[#E0E6ED] p-5 space-y-4">
          <SkeletonBox className="h-5 w-28 rounded-md" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <SkeletonBox className="h-16 rounded-2xl" />
            <SkeletonBox className="h-16 rounded-2xl" />
          </div>
          <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-100">
            {[1, 2, 3, 4].map((i) => (
              <SkeletonBox key={i} className="h-8 w-24 rounded-xl" />
            ))}
          </div>
        </div>

        {/* Alamat Section */}
        <div className="bg-white rounded-3xl border border-[#E0E6ED] p-5 space-y-3">
          <SkeletonBox className="h-4 w-16 rounded-md" />
          <div className="flex items-start gap-2.5">
            <SkeletonBox className="w-4 h-4 rounded-full shrink-0 mt-0.5" />
            <div className="space-y-1.5 flex-1">
              <SkeletonBox className="h-4 w-3/4 rounded-md" />
              <SkeletonBox className="h-3.5 w-1/2 rounded-md" />
            </div>
          </div>
          <SkeletonBox className="w-full h-36 rounded-2xl" />
        </div>

        {/* Deskripsi & Penyelenggara */}
        <div className="bg-white rounded-3xl border border-[#E0E6ED] p-5 space-y-3">
          <SkeletonBox className="h-4 w-24 rounded-md" />
          <SkeletonBox className="h-3.5 w-full rounded-md" />
          <SkeletonBox className="h-3.5 w-5/6 rounded-md" />
        </div>
      </div>

      {/* Right Column (Desktop Sticky Join Card) */}
      <div className="hidden lg:block lg:col-span-5 xl:col-span-4 space-y-4">
        <div className="bg-white rounded-3xl border border-[#E0E6ED] p-6 space-y-5 shadow-xs">
          <div className="space-y-1.5">
            <SkeletonBox className="h-3 w-16 rounded-md" />
            <SkeletonBox className="h-8 w-36 rounded-md bg-orange-100/70" />
            <SkeletonBox className="h-4 w-32 rounded-md" />
          </div>
          <div className="border-t border-gray-100 pt-4 space-y-3">
            <SkeletonBox className="h-4 w-full rounded-md" />
            <SkeletonBox className="h-4 w-full rounded-md" />
          </div>
          <SkeletonBox className="h-12 w-full rounded-full bg-orange-200/70" />
        </div>
      </div>
    </div>

    {/* Mobile Sticky Bottom Action Bar Skeleton */}
    <div className="lg:hidden fixed bottom-0 left-0 right-0 max-w-md mx-auto bg-white/95 backdrop-blur-md border-t border-gray-100 p-3.5 px-4 z-40 flex items-center gap-3 shadow-lg">
      <SkeletonBox className="w-12 h-12 rounded-full shrink-0" />
      <SkeletonBox className="flex-1 h-12 rounded-full bg-orange-100/70" />
    </div>
  </div>
);

/**
 * MabarParticipantsSkeleton - Matches the exact layout of ClientMabarParticipantsPage page
 */
export const MabarParticipantsSkeleton: React.FC = () => (
  <div className="bg-[#F8FAFC] min-h-screen pb-12 pt-4 px-4 max-w-md mx-auto space-y-5 animate-pulse">
    {/* TOP STATS CARD SKELETON */}
    <div className="bg-white rounded-3xl p-4 sm:p-5 border border-gray-100 shadow-2xs grid grid-cols-3 divide-x divide-gray-100 text-center">
      {[1, 2, 3].map((i) => (
        <div key={i} className="px-2 space-y-1.5 flex flex-col items-center justify-center">
          <SkeletonBox className="h-5 w-10 rounded-md bg-orange-100/60" />
          <SkeletonBox className="h-3 w-16 rounded-md" />
        </div>
      ))}
    </div>

    {/* SECTION 1: SUDAH BERGABUNG SKELETON */}
    <div className="space-y-2.5">
      <SkeletonBox className="h-5 w-36 rounded-md px-1" />
      <div className="bg-white rounded-3xl border border-gray-100 shadow-2xs divide-y divide-gray-100 overflow-hidden">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <div key={i} className="p-3.5 px-4 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 flex-1">
              <SkeletonBox className="w-5 h-4 rounded-md shrink-0" />
              <SkeletonBox className="w-10 h-10 rounded-full shrink-0" />
              <div className="space-y-1.5 flex-1">
                <SkeletonBox className="h-4 w-32 rounded-md" />
                <SkeletonBox className="h-3 w-24 rounded-md" />
              </div>
            </div>
            <SkeletonBox className="h-7 w-20 rounded-full bg-orange-100/60 shrink-0" />
          </div>
        ))}
      </div>
    </div>

    {/* SECTION 2: SLOT TERSISA SKELETON */}
    <div className="space-y-2.5">
      <SkeletonBox className="h-5 w-28 rounded-md px-1" />
      <div className="bg-white rounded-3xl border border-gray-100 shadow-2xs divide-y divide-gray-100 overflow-hidden">
        {[1, 2, 3].map((i) => (
          <div key={i} className="p-3.5 px-4 flex items-center gap-3">
            <SkeletonBox className="w-5 h-4 rounded-md shrink-0" />
            <SkeletonBox className="w-10 h-10 rounded-full shrink-0" />
            <SkeletonBox className="h-4 w-28 rounded-md" />
          </div>
        ))}
      </div>
    </div>
  </div>
);

